/**
 * Unified Add to Cart Utility
 * Fixes Shopify API compatibility issues after August 28, 2025 changes
 * Replaces multiple inconsistent implementations with single, reliable function
 */

class CartUtils {
  constructor() {
    this.isLoading = false;
    this.retryAttempts = 3;
    this.retryDelay = 1000;
  }

  /**
   * Unified add to cart function
   * @param {Object} options - Configuration options
   * @param {string|number} options.id - Product variant ID
   * @param {number} options.quantity - Quantity to add (default: 1)
   * @param {Object} options.properties - Product properties (optional)
   * @param {Function} options.onSuccess - Success callback
   * @param {Function} options.onError - Error callback
   * @param {Function} options.onLoading - Loading state callback
   * @param {string} options.context - Context for debugging (e.g., 'product-form', 'cart-drawer')
   */
  async addToCart({
    id,
    quantity = 1,
    properties = {},
    onSuccess = () => {},
    onError = () => {},
    onLoading = () => {},
    context = 'unknown'
  }) {
    // Prevent multiple simultaneous requests
    if (this.isLoading) {
      console.warn(`CartUtils: Add to cart already in progress (${context})`);
      return;
    }

    this.isLoading = true;
    onLoading(true);

    try {
      const response = await this._makeAddToCartRequest(id, quantity, properties, context);
      
      if (response.ok) {
        const data = await response.json();
        await this._handleSuccess(data, context);
        onSuccess(data);
      } else {
        const errorData = await response.json();
        this._handleError(errorData, context);
        onError(errorData);
      }
    } catch (error) {
      console.error(`CartUtils: Add to cart failed (${context}):`, error);
      this._handleError({ description: 'Network error occurred' }, context);
      onError({ description: 'Network error occurred' });
    } finally {
      this.isLoading = false;
      onLoading(false);
    }
  }

  /**
   * Make the actual add to cart request with proper error handling
   */
  async _makeAddToCartRequest(id, quantity, properties, context) {
    // Use simple JSON payload as per Shopify's official example
    const payload = {
      id: parseInt(id),
      quantity: parseInt(quantity)
    };

    // Add properties if provided
    if (Object.keys(properties).length > 0) {
      payload.properties = properties;
    }

    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(payload)
    };

    // Use the correct endpoint
    const endpoint = this._getCartAddEndpoint();
    
    console.log(`CartUtils: Adding to cart (${context}):`, payload);
    
    return fetch(endpoint, requestOptions);
  }

  /**
   * Get the correct cart add endpoint
   */
  _getCartAddEndpoint() {
    // Always use the .js endpoint for JSON requests as per Shopify's official example
    if (window.routes && window.routes.cart_add_url) {
      const baseUrl = window.routes.cart_add_url;
      return baseUrl.endsWith('.js') ? baseUrl : baseUrl + '.js';
    }
    
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root + 'cart/add.js';
    }
    
    // Fallback to direct path
    return '/cart/add.js';
  }

  /**
   * Handle successful add to cart
   */
  async _handleSuccess(data, context) {
    console.log(`CartUtils: Successfully added to cart (${context}):`, data);
    
    // Update cart count if element exists
    this._updateCartCount();
    
    // Update cart drawer if it exists
    await this._updateCartDrawer();
    
    // Publish cart update event for other components
    this._publishCartUpdateEvent(data);
  }

  /**
   * Handle add to cart errors
   */
  _handleError(errorData, context) {
    console.error(`CartUtils: Add to cart error (${context}):`, errorData);
    
    // Handle specific error types
    if (errorData.status === 422) {
      // Validation error (e.g., sold out, quantity limit)
      this._showError(errorData.description || 'Unable to add item to cart');
    } else if (errorData.status === 404) {
      // Product not found
      this._showError('Product not found');
    } else {
      // Generic error
      this._showError(errorData.description || 'An error occurred while adding to cart');
    }
  }

  /**
   * Update cart count in header
   */
  _updateCartCount() {
    const cartCountElement = document.querySelector('.cart-count-bubble span');
    if (cartCountElement) {
      const currentCount = parseInt(cartCountElement.textContent) || 0;
      cartCountElement.textContent = currentCount + 1;
    }
  }

  /**
   * Update cart drawer content
   */
  async _updateCartDrawer() {
    try {
      const response = await fetch('/?snippets=cart-drawer');
      const html = await response.text();
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const cartDrawer = doc.querySelector('#CartDrawer');
      
      if (cartDrawer) {
        const existingCartDrawer = document.querySelector('#CartDrawer');
        if (existingCartDrawer) {
          existingCartDrawer.innerHTML = cartDrawer.innerHTML;
        }
      }
    } catch (error) {
      console.warn('CartUtils: Failed to update cart drawer:', error);
    }
  }

  /**
   * Publish cart update event for other components
   */
  _publishCartUpdateEvent(data) {
    // Dispatch custom event for other components to listen to
    const event = new CustomEvent('cart:updated', {
      detail: { data, timestamp: Date.now() }
    });
    document.dispatchEvent(event);
  }

  /**
   * Show error message to user
   */
  _showError(message) {
    // Try to find error container
    const errorContainer = document.querySelector('.product-form__error-message') || 
                          document.querySelector('.popup_error_container') ||
                          document.querySelector('.error_handler');
    
    if (errorContainer) {
      errorContainer.textContent = message;
      errorContainer.style.display = 'block';
      
      // Hide error after 5 seconds
      setTimeout(() => {
        errorContainer.style.display = 'none';
      }, 5000);
    } else {
      // Fallback to alert if no error container found
      alert(message);
    }
  }

  /**
   * Quick add function for simple use cases
   * @param {string|number} variantId - Product variant ID
   * @param {number} quantity - Quantity to add
   * @param {string} context - Context for debugging
   */
  quickAdd(variantId, quantity = 1, context = 'quick-add') {
    return this.addToCart({
      id: variantId,
      quantity,
      context
    });
  }

  /**
   * Add multiple items to cart
   * @param {Array} items - Array of items with id and quantity
   * @param {string} context - Context for debugging
   */
  async addMultipleToCart(items, context = 'bulk-add') {
    const results = [];
    
    for (const item of items) {
      try {
        await this.addToCart({
          id: item.id,
          quantity: item.quantity,
          properties: item.properties || {},
          context: `${context}-${item.id}`
        });
        results.push({ success: true, item });
      } catch (error) {
        results.push({ success: false, item, error });
      }
    }
    
    return results;
  }
}

// Create global instance
window.CartUtils = new CartUtils();

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CartUtils;
}
